import React, { useState } from "react";
import { FaEye, FaFilePdf, FaFileWord, FaDownload } from "react-icons/fa";
import { RxCross2 } from "react-icons/rx";
import { MdDelete } from "react-icons/md";

/**
 * ImagePreview Component
 * A reusable component for displaying images with preview functionality
 *
 * @param {Object} props - Component props
 * @param {string|Array|Object} props.data - Image data (string URL, array of URLs, or object with url property)
 * @param {number} props.maxVisible - Maximum number of images to show before showing "+X more" (default: 3)
 * @param {string} props.className - Additional CSS classes for the container
 * @param {string} props.imageClassName - Additional CSS classes for individual images
 * @param {string} props.placeholder - Text to show when no images are available (default: "-")
 * @param {boolean} props.showPreview - Whether to show preview modal on click (default: true)
 * @param {string} props.previewTitle - Title for the preview modal
 * @param {function} props.onImageClick - Custom click handler for images
 * @param {Object} props.imageSize - Size configuration for images {width, height} (default: {width: 32, height: 32})
 * @param {boolean} props.showHoverEffect - Whether to show hover effects (default: true)
 * @param {function} props.onDelete - Function to handle image deletion (index, imageUrl)
 * @param {boolean} props.showDeleteIcon - Whether to show delete icon (default: false)
 * @param {number} props.deletingIndex - Index of image being deleted (for loading state)
 *
 * @example
 * // Basic usage with single image
 * <ImagePreview data="https://example.com/image.jpg" />
 *
 * @example
 * // Multiple images with custom max visible
 * <ImagePreview
 *   data={["url1.jpg", "url2.jpg", "url3.jpg", "url4.jpg"]}
 *   maxVisible={2}
 * />
 *
 * @example
 * // Custom styling and size
 * <ImagePreview
 *   data={imageData}
 *   imageSize={{width: 48, height: 48}}
 *   className="my-custom-class"
 *   imageClassName="rounded-lg"
 * />
 *
 * @example
 * // With custom click handler
 * <ImagePreview
 *   data={imageData}
 *   showPreview={false}
 * />
 *
 * @example
 * // With delete functionality
 * <ImagePreview
 *   data={imageData}
 *   onDelete={(index, imageUrl) => handleDeleteImage(index, imageUrl)}
 *   showDeleteIcon={true}
 * />
 */
const ImagePreview = ({
  data,
  maxVisible = 3,
  className = "",
  imageClassName = "",
  placeholder = "-",
  showPreview = true,
  previewTitle = "Image Preview",
  onImageClick,
  imageSize = { width: 32, height: 32 },
  showHoverEffect = true,
  maxWidth = "max-w-60",
  onDelete,
  showDeleteIcon = false,
  deletingIndex = null,
}) => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Helper function to get file extension from URL
  const getFileExtension = (url) => {
    if (!url || typeof url !== "string") return "";
    const match = url.match(/\.([a-zA-Z0-9]+)(?:[?#]|$)/);
    return match ? match[1].toLowerCase() : "";
  };

  // Check if file is PDF or DOC
  const isDocumentFile = (url) => {
    const extension = getFileExtension(url);
    return ["pdf", "doc", "docx"].includes(extension);
  };

  const handleImageClick = (imageUrl, imageName = "Image") => {
    // If it's a PDF or DOC file, open in new tab
    if (isDocumentFile(imageUrl)) {
      window.open(imageUrl, "_blank");
      return;
    }

    if (onImageClick) {
      onImageClick(imageUrl);
    } else if (showPreview) {
      setSelectedImage({ url: imageUrl, name: imageName });
      setShowPreviewModal(true);
    }
  };

  const closePreview = () => {
    setShowPreviewModal(false);
    setSelectedImage(null);
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (!selectedImage) return;

    try {
      // Fetch the image as a blob to handle CORS issues
      const response = await fetch(selectedImage.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = selectedImage.name || `image.${getFileExtension(selectedImage.url) || "jpg"}`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading image:", error);
      // Fallback: open in new tab if download fails
      window.open(selectedImage.url, "_blank");
    }
  };

  const handleDeleteClick = (e, index, imageUrl) => {
    e.stopPropagation(); // Prevent image click when delete is clicked
    if (onDelete) {
      onDelete(index, imageUrl);
    }
  };

  // Handle different data formats
  const normalizeImages = (data) => {
    if (!data) return [];

    if (Array.isArray(data)) {
      return data.filter((img) => img && (typeof img === "string" || img.url));
    }

    if (typeof data === "string") {
      return [data];
    }

    if (data.url) {
      return [data];
    }

    return [];
  };

  const images = normalizeImages(data);
  const visibleImages = images.slice(0, maxVisible);
  const remainingCount = images.length - maxVisible;

  const imageStyle = {
    width: `${imageSize.width}px`,
    height: `${imageSize.height}px`,
  };

  return (
    <div className={`flex items-center gap-2 ${maxWidth} ${className}`}>
      {images.length > 0 ? (
        <div className="flex gap-1 flex-wrap">
          {visibleImages.map((img, index) => {
            const imageUrl = typeof img === "string" ? img : img.url;
            const imageName = img.name || `Image ${index + 1}`;
            const isDocument = isDocumentFile(imageUrl);
            const fileExtension = getFileExtension(imageUrl);

            return (
              <div
                key={index}
                className={`relative rounded border border-gray-200 overflow-hidden cursor-pointer group ${imageClassName} ${
                  deletingIndex === index
                    ? "opacity-50 pointer-events-none"
                    : ""
                }`}
                style={imageStyle}
                onClick={() => handleImageClick(imageUrl, imageName)}
                title={isDocument ? "Click to open in new tab" : "Click to preview"}
              >
                {isDocument ? (
                  // Show icon for PDF/DOC files
                  <div className="w-full h-full bg-gray-50 flex items-center justify-center">
                    {fileExtension === "pdf" ? (
                      <FaFilePdf className="text-red-600" style={{ fontSize: `${Math.min(imageSize.width, imageSize.height) * 0.6}px` }} />
                    ) : (
                      <FaFileWord className="text-blue-600" style={{ fontSize: `${Math.min(imageSize.width, imageSize.height) * 0.6}px` }} />
                    )}
                  </div>
                ) : (
                  // Show image for image files
                  <img
                    src={imageUrl}
                    alt={imageName}
                    className="w-full h-full object-cover group-hover:opacity-70 transition-opacity duration-200"
                  />
                )}

                {/* Loading overlay when deleting */}
                {deletingIndex === index && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  </div>
                )}

                {/* Delete icon in top-right corner */}
                {showDeleteIcon && onDelete && deletingIndex !== index && (
                  <button
                    onClick={(e) => handleDeleteClick(e, index, imageUrl)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600 z-10"
                    title="Delete image"
                  >
                    <MdDelete className="text-xs" />
                  </button>
                )}

                {/* Eye icon overlay on hover (only for images, not documents) */}
                {showHoverEffect && !isDocument && deletingIndex !== index && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-all duration-200">
                    <FaEye className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-sm" />
                  </div>
                )}
              </div>
            );
          })}
          {remainingCount > 0 && (
            <div
              className="bg-gray-100 rounded flex items-center justify-center text-xs text-gray-600 border border-gray-200"
              style={imageStyle}
            >
              +{remainingCount}
            </div>
          )}
        </div>
      ) : (
        <span className="text-gray-400 text-sm">{placeholder}</span>
      )}

      {/* Image Preview Modal */}
      {showPreviewModal && selectedImage && (
        <div className="fixed w-full h-full inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-20">
          {/* Action buttons container */}
          <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
            <button
              onClick={handleDownload}
              className="text-white rounded-full p-2.5 bg-black bg-opacity-50 hover:bg-opacity-70 transition-all duration-200 flex items-center justify-center shadow-lg"
              title="Download image"
            >
              <FaDownload className="text-lg" />
            </button>
            <button
              onClick={closePreview}
              className="text-white rounded-full p-2.5 bg-black bg-opacity-50 hover:bg-opacity-70 transition-all duration-200 flex items-center justify-center shadow-lg"
              title="Close preview"
            >
              <RxCross2 className="text-xl" />
            </button>
          </div>
          <img
            src={selectedImage.url}
            alt={selectedImage.name}
            className="w-[100%] h-[100%] object-contain"
          />
        </div>
      )}
    </div>
  );
};

export default ImagePreview;
